import { useRecordContext, useDataProvider } from "react-admin";
import { FetchData } from "./dataFetcher";
import { useEffect, useState, useRef } from "react";
import * as d3 from "d3";
import { bwColours } from "../lib/theme";

const containerHeight = 27;
const containerWidth = 300;
const margin = { top: 0, left: 0, bottom: 0, right: 0 };

const styles = {
  svg: {
    width: containerWidth,
    // Table cells on a phone are narrower than the 300px canvas; let
    // the sparkline shrink instead of being clipped.
    maxWidth: "100%",
    height: containerHeight,
  },
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
};

const VCSparkline = ({
  label,
  textAlign,
}) => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!record) return;

    const apiFilter = {
      ids: record?.eventFSO2ReadIDs,
    };

    FetchData(
      dataProvider,
      setData,
      setLoading,
      (err) => {
        if (err) console.log(err);
      },
      apiFilter,
    );
  }, [record, dataProvider]);

  useEffect(() => {
    if (!record || !data) return;

    let canvas = d3.select(svgRef.current);

    // Define viewBox
    canvas.attr("viewBox", `0 0 ${containerWidth} ${containerHeight}`);

    let yScale = d3
      .scaleLinear()
      .domain([-1, 1])
      .range([margin.top, containerHeight - margin.bottom]);

    let xScale = d3
      .scaleLinear()
      .domain([-1, 64])
      .range([margin.left + 3, containerWidth - margin.right]);

    // 0 line

    canvas
      .append("line")
      .attr("x1", xScale(0))
      .attr("y1", yScale(-1))
      .attr("x2", xScale(0))
      .attr("y2", yScale(+1))
      .attr("stroke", "#ddd")
      .attr("stroke-width", 0.5);

    // 60 line
    canvas
      .append("line")
      .attr("x1", xScale(60))
      .attr("y1", yScale(-1))
      .attr("x2", xScale(60))
      .attr("y2", yScale(+1))
      .attr("stroke", "#ddd")
      .attr("stroke-width", 0.5);

    // Indicate each measurement (that is in range)
    canvas
      .selectAll("circle")
      .data(data.filter((d) => d.fso2 <= 60))
      .enter()
      .append("circle")
      .attr("cy", (d) => yScale(0))
      .attr("cx", (d) => xScale(d.fso2))
      .attr("r", 5)
      .style("fill", bwColours.primary.red)
      .style("opacity", 0.3)
      .attr("clip-path", "url(#clip)");

    // Indicate data points over 60
    if (data.some((d) => d.fso2 > 60)) {
      canvas
        .append("text")
        .attr("x", xScale(61.5))
        .attr("y", yScale(0.095))
        .attr("text-anchor", "middle") // center horizontally
        .attr("alignment-baseline", "middle") // center vertically
        .attr("font-size", "24px")
        .style("fill", bwColours.primary.red)
        .style("opacity", 0.5)
        .text("»");
    }

    // Average PPM
    if (record.fso2Avg <= 60) {
      canvas
        .append("line")
        .attr("x1", xScale(record.fso2Avg))
        .attr("y1", yScale(-1))
        .attr("x2", xScale(record.fso2Avg))
        .attr("y2", yScale(+1))
        .attr("stroke", "black")
        .attr("stroke-width", 1);
    }
  }, [data, record, loading]);

  return (
    <div style={styles.container}>
      {loading ? null : <svg ref={svgRef} style={styles.svg} />}
    </div>
  );
};

export default VCSparkline;
